import React, { useCallback, useRef, useState } from "react";
import { PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen } from "lucide-react";
import { cn } from "@/lib/utils";

export function usePanel(storageKey, defaultWidth) {
  const read = (k, d) => { try { const v = JSON.parse(localStorage.getItem(k)); return v == null ? d : v; } catch { return d; } };
  const [width, setWidth] = useState(() => read(`${storageKey}_w`, defaultWidth));
  const [collapsed, setCollapsed] = useState(() => read(`${storageKey}_c`, false));
  const set = useCallback((w, c) => {
    if (w != null) { setWidth(w); localStorage.setItem(`${storageKey}_w`, JSON.stringify(w)); }
    if (c != null) { setCollapsed(c); localStorage.setItem(`${storageKey}_c`, JSON.stringify(c)); }
  }, [storageKey]);
  return { width, collapsed, setWidth: (w) => set(w, null), setCollapsed: (c) => set(null, c) };
}

// side: which edge of the SCREEN the panel sits on ("left" => resize handle on its right edge)
export function ResizableAside({ side, panel, min = 180, max = 520, testid, className, children }) {
  const { width, collapsed, setWidth, setCollapsed } = panel;
  const dragRef = useRef(null);

  const onPointerDown = (e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startW: width };
    const onMove = (ev) => {
      const d = ev.clientX - dragRef.current.startX;
      const next = side === "left" ? dragRef.current.startW + d : dragRef.current.startW - d;
      setWidth(Math.max(min, Math.min(max, Math.round(next))));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const CollapseIcon = side === "left" ? PanelLeftClose : PanelRightClose;
  const ExpandIcon = side === "left" ? PanelLeftOpen : PanelRightOpen;

  if (collapsed) {
    return (
      <aside data-testid={`${testid}-collapsed`} data-chrome="pane" className={cn("w-7 shrink-0 flex flex-col items-center pt-2 bg-[color:var(--glass-bg)]",
        side === "left" ? "border-r" : "border-l", "border-[color:var(--glass-border)]", className)}>
        <button data-testid={`${testid}-expand-btn`} title="Expand panel" onClick={() => setCollapsed(false)}
          className="p-1 rounded-md text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]">
          <ExpandIcon className="w-4 h-4" />
        </button>
      </aside>
    );
  }

  return (
    <aside data-testid={testid} data-chrome="pane" style={{ width }} className={cn("relative shrink-0 bg-[color:var(--glass-bg)] backdrop-blur-sm",
      side === "left" ? "border-r" : "border-l", "border-[color:var(--glass-border)]", className)}>
      <button data-testid={`${testid}-collapse-btn`} title="Collapse panel" onClick={() => setCollapsed(true)}
        className={cn("absolute top-1.5 z-20 p-1 rounded-md text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]",
          side === "left" ? "right-1.5" : "left-1.5")}>
        <CollapseIcon className="w-3.5 h-3.5" />
      </button>
      <div className="h-full">{children}</div>
      <div data-testid={`${testid}-resize-handle`} onPointerDown={onPointerDown}
        className={cn("absolute top-0 bottom-0 w-1.5 cursor-col-resize z-20 hover:bg-[color:rgba(43,78,255,0.25)] transition-colors",
          side === "left" ? "-right-0.5" : "-left-0.5")} />
    </aside>
  );
}
